// 組織樹畫面共用的查詢（react-query key 統一，改動後 invalidate 用 ['ot', org_id]）。
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useApp } from '../../lib/AppContext';
import { orgApi, type MyOrg, type OrgRef } from '../../lib/planeApi';
import { meTreeApi, treeApi, type DeviceGroup, type View } from '../../lib/orgTreeApi';

export function useMeId(): string {
  return useApp().session?.user?.user_id ?? '';
}

export function useOrgMembers(org: OrgRef, enabled = true) {
  return useQuery({ queryKey: ['ot', org.org_id, 'members'], queryFn: () => orgApi.members(org), enabled, staleTime: 60_000 });
}

export function useMemberGroups(org: OrgRef, enabled = true) {
  return useQuery({ queryKey: ['ot', org.org_id, 'member-groups'], queryFn: () => treeApi.memberGroups(org), enabled, staleTime: 30_000, retry: false });
}

export function useRoleTemplates(org: OrgRef, enabled = true) {
  return useQuery({
    queryKey: ['ot', org.org_id, 'role-templates'],
    queryFn: () => orgApi.roles(org).then((r) => (r?.roles ?? []).filter((x) => !x.system && x.role_kind === 'grant')),
    enabled,
    staleTime: 60_000,
    retry: false,
  });
}

export function useTreeSettings(org: OrgRef) {
  return useQuery({ queryKey: ['ot', org.org_id, 'tree-settings'], queryFn: () => treeApi.settings(org), staleTime: 60_000, retry: false });
}

/** 組織樹的資料改了：結構、存取、我的樹、我的設備都重抓。 */
export function useTreeRefresh(org: Pick<MyOrg, 'org_id'>) {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ['ot', org.org_id] });
    void qc.invalidateQueries({ queryKey: ['me-tree'] });
    void qc.invalidateQueries({ queryKey: ['me-devices'] });
  };
}

/** 自我授權黃框要寫誰會收到通知（SPEC #org-tree-ui 第 2 點 ux-2-11）。 */
export function selfNotifyKey(org: Pick<MyOrg, 'role'>, members: { role: string; is_me?: boolean; status?: string }[] | undefined): 'others' | 'owner' | 'audit_only' {
  const others = (members ?? []).filter((m) => !m.is_me && m.status !== 'suspended' && (m.role === 'org_admin' || m.role === 'owner'));
  if (others.some((m) => m.role === 'org_admin')) return 'others';
  if (others.some((m) => m.role === 'owner')) return 'owner';
  return org.role === 'owner' ? 'audit_only' : 'owner';
}

/** 我看得到的檢視與設備群組（個人 + 我所屬每個組織的；各 plane 合併）。 */
export function useViewsAndGroups(planes: { api: string }[], orgs: OrgRef[]) {
  const views = useQuery({
    queryKey: ['me-tree', 'views', planes.map((p) => p.api).join(','), orgs.map((o) => o.org_id).join(',')],
    queryFn: async () => {
      // _api：這筆檢視在哪個 plane（加到檢視時要寫回同一個 plane，不是來源項目的 plane）
      const mine = (await Promise.all(planes.map((p) => meTreeApi.views(p).then((xs) => xs.map((x) => ({ ...x, _api: p.api }))).catch(() => [] as View[])))).flat();
      const org = (await Promise.all(orgs.map((o) => meTreeApi.orgViews(o).then((xs) => xs.map((x) => ({ ...x, _api: o.api }))).catch(() => [] as View[])))).flat();
      return dedupe([...mine, ...org], (v) => v.view_id);
    },
    staleTime: 30_000,
  });
  const groups = useQuery({
    queryKey: ['me-tree', 'groups', planes.map((p) => p.api).join(','), orgs.map((o) => o.org_id).join(',')],
    queryFn: async () => {
      const mine = (await Promise.all(planes.map((p) => meTreeApi.groups(p).then((xs) => xs.map((x) => ({ ...x, _api: p.api }))).catch(() => [] as DeviceGroup[])))).flat();
      const org = (await Promise.all(orgs.map((o) => meTreeApi.orgGroups(o).then((xs) => xs.map((x) => ({ ...x, _api: o.api }))).catch(() => [] as DeviceGroup[])))).flat();
      return dedupe([...mine, ...org], (g) => g.group_id);
    },
    staleTime: 30_000,
  });
  return { views, groups };
}

function dedupe<T>(xs: T[], key: (x: T) => string): T[] {
  const seen = new Set<string>();
  return xs.filter((x) => (seen.has(key(x)) ? false : (seen.add(key(x)), true)));
}
